import { ErrorModal, useMesfRealtime } from "@dexteel/mesf-core";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, IconButton, TextField } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { ReadyRollset } from "../../models/Rolllset";
import { getReadyRollsets } from "../../repositories/RollsetRepository";
import { RollsetModalSelector } from "./RollsetModalSelector";

type useRollsetsProps = {
	standId?: number | null;
	sectionId?: number | null;
	rollsetId?: number | null;
};

export const useGetReadyRollsets = ({
	standId,
	sectionId,
	rollsetId,
}: useRollsetsProps) => {
	return useQuery<ReadyRollset[], Error>({
		queryKey: ["readyRollsets", standId, sectionId, rollsetId],
		queryFn: ({ signal }) => {
			if (!sectionId) {
				return [];
			}
			return getReadyRollsets({ standId, sectionId, rollsetId }, signal);
		},
	});
};

type Props = {
	label?: string;
	value: number | null;
	onChange: (value: number | null) => void;
	standId: number | null;
	standName: string | null;
	sectionId: number | null;
	disabled?: boolean;
};

export const RollsetSelector = ({
	label,
	value,
	onChange,
	sectionId,
	standId,
	standName,
	disabled = false,
}: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [message, setMessage] = useState("");
	const [rollsetId, setRollsetId] = useState<number | null>(null);

	const { data: rollsets, isLoading } = useGetReadyRollsets({
		standId,
		sectionId,
		rollsetId: rollsetId || value,
	});

	const sectionIdRef = useRef(0);
	sectionIdRef.current = sectionId || 0;
	const standIdRef = useRef(0);
	standIdRef.current = standId || 0;

	const selectedValue = rollsets?.find((r) => r.RollsetId === value);

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") == "M2.GetReadyRollsets"
			) {
				await queryClient.invalidateQueries({
					queryKey: ["readyRollsets", standIdRef.current, sectionIdRef.current],
				});
			}
		},
	});

	return (
		<>
			<TextField
				color="primary"
				variant="outlined"
				onClick={() => {
					if (disabled) return;
					const missStandOrSection =
						showModal && (sectionId == null || standId == null);
					setShowModal(true);
					setMessage(
						missStandOrSection ? "Please first choose section and stand" : "",
					);
				}}
				size="small"
				disabled={disabled}
				fullWidth
				label={
					label
						? label
						: `Rollset ${selectedValue?.SectionName ?? ""}-${
								selectedValue?.RollsetLetter ?? ""
							}`
				}
				multiline
				minRows={3}
				maxRows={3}
				value={`${selectedValue?.TopRollName ?? ""}\n${
					selectedValue?.BottomRollName ?? ""
				}\n${selectedValue?.TopRollDiameter ?? ""}`}
				slotProps={{
					htmlInput: { "data-test-id": "rollset-selector" },
				}}
				InputProps={{
					readOnly: true,
					endAdornment: isLoading ? (
						<CircularProgress color="inherit" size={20} />
					) : value ? (
						<IconButton
							onClick={(event) => {
								event.stopPropagation();
								onChange(null);
							}}
							size="small"
							title="Clear selection"
							disabled={disabled}
						>
							<CloseIcon />
						</IconButton>
					) : null,
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading rollsets"
			/>
			<ErrorModal error={message} onHide={() => setMessage("")} />
			<RollsetModalSelector
				value={value}
				onChange={onChange}
				standId={standId}
				standName={standName}
				sectionId={sectionId}
				show={showModal && sectionId != null && standId != null}
				onHide={() => setShowModal(false)}
				onNewRollsetSaved={(newRollsetId) => setRollsetId(newRollsetId)}
				rollsets={rollsets || []}
				isLoading={isLoading}
			/>
		</>
	);
};
